import CryptoJS from 'crypto-js';
import { deleteAllCookies } from 'smart-react';

// Secret key for encryption - In production, use environment variables
const SECRET_KEY = process.env.ENCRYPTION_KEY || 'Secret-Key';

/**
 * Encrypts data and stores it in localStorage or sessionStorage
 * @param {string} key - The storage key
 * @param {any} data - The data to encrypt and store (will be JSON stringified)
 * @param {boolean} useSessionStorage - Whether to use sessionStorage instead of localStorage (default: false)
 * @returns {boolean} - Returns true if successful, false if failed
 */
export const encryptAndStore = (key, data, useSessionStorage = false) => {
  try {
    // Convert data to JSON string
    const jsonString = JSON.stringify(data);

    // Encrypt the data
    const encrypted = CryptoJS.AES.encrypt(jsonString, SECRET_KEY).toString();

    // Store in appropriate storage
    const storage = useSessionStorage ? sessionStorage : localStorage;
    storage.setItem(key, encrypted);

    return true;
  } catch (error) {
    console.error('Error encrypting and storing data:', error);
    return false;
  }
};

/**
 * Retrieves data from localStorage/sessionStorage and decrypts it
 * @param {string} key - The storage key
 * @param {boolean} useSessionStorage - Whether to use sessionStorage instead of localStorage (default: false)
 * @returns {any|null} - Returns decrypted data or null if failed/not found
 */
export const retrieveAndDecrypt = (key, useSessionStorage = false) => {
  try {
    // Get encrypted data from appropriate storage
    const storage = useSessionStorage ? sessionStorage : localStorage;
    const encryptedData = storage.getItem(key);

    if (!encryptedData) {
      return null; // Key doesn't exist
    }

    // Check if data appears to be encrypted (basic validation)
    if (!isEncryptedData(encryptedData)) {
      console.warn('Data is not in encrypted format, redirecting to login');
      redirectToLogin();
      return null;
    }

    // Decrypt the data
    const decryptedBytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
    const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);

    if (!decryptedString) {
      throw new Error('Failed to decrypt data - possibly wrong key');
    }

    // Parse JSON and return
    return JSON.parse(decryptedString);
  } catch (error) {
    console.error('Error retrieving and decrypting data:', error);
    return null;
  }
};

/**
 * Removes encrypted data from localStorage
 * @param {string} key - The localStorage key to remove
 * @returns {boolean} - Returns true if successful
 */
export const removeEncryptedData = (key) => {
  try {
    localStorage.removeItem(key);
    return true;
  } catch (error) {
    console.error('Error removing encrypted data:', error);
    return false;
  }
};

/**
 * Checks if an encrypted key exists in localStorage
 * @param {string} key - The localStorage key to check
 * @returns {boolean} - Returns true if key exists
 */
export const hasEncryptedData = (key) => {
  return localStorage.getItem(key) !== null;
};

/**
 * Helper function to check if data appears to be encrypted
 * @param {string} data - The data to check
 * @returns {boolean} - Returns true if data appears encrypted
 */
const isEncryptedData = (data) => {
  // Basic checks for encrypted data format
  // CryptoJS AES encrypted data is typically base64 encoded and has specific characteristics

  // Check if it's a valid base64 string
  const base64Regex = /^[A-Za-z0-9+/]*={0,2}$/;
  if (!base64Regex.test(data)) {
    return false;
  }

  // Check minimum length (encrypted data should be reasonably long)
  if (data.length < 20) {
    return false;
  }

  // Try to decode as base64 - if it fails, it's likely not encrypted
  try {
    const decoded = atob(data);
    // Encrypted data should contain non-printable characters
    return decoded.length > 0;
  } catch (e) {
    return false;
  }
};

/**
 * Helper function to redirect to login page
 */
const redirectToLogin = () => {
  // Clear potentially corrupted data
  sessionStorage.clear();
  localStorage.clear();
  deleteAllCookies();
  window.location.replace('/login');
};

/**
 * Helper function to check if data appears to be encrypted
 * @param {string} data - The data to check
 * @returns {boolean} - Returns true if data appears encrypted
 */
export const getLookupFromLocalStorage = ({ type }) => {
  try {
    const data = retrieveAndDecrypt(type);
    return data !== null ? data : false;
  } catch (error) {
    console.error('Error accessing localStorage:', error);
    return false;
  }
};
